<template>
  <div class="alice-files">
    <!-- ============== Sub-page header ============== -->
    <header class="alice-files__head">
      <div class="alice-files__head-main">
        <div class="alice-files__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Files</span>
        </div>
        <h1 class="alice-files__title">
          Files
          <span class="alice-files__count">{{ files.length }}</span>
        </h1>
        <p class="alice-files__subtitle">
          Onderzoeksbestanden — testscripts, intranet-PDFs en het eindrapport.
          Drop nieuwe PDFs in de zone hieronder of klik om te uploaden.
        </p>
      </div>

      <button
        class="alice-files__upload"
        :disabled="anyUploading"
        @click="triggerUpload"
      >
        <Icon name="plus" :size="16" />
        <span>Upload file</span>
      </button>
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,application/pdf"
        multiple
        style="display: none"
        @change="onFileChange"
      />
    </header>

    <!-- ============== Stats trio ============== -->
    <div class="alice-files__stats">
      <div class="alice-files__stat-tile">
        <div class="alice-files__stat-row">
          <span
            class="alice-files__stat-mark"
            style="background: var(--vp-black)"
          />
          <span class="alice-files__stat-value">{{ files.length }}</span>
        </div>
        <div class="alice-files__stat-label">Total files</div>
        <div class="alice-files__stat-sub">
          {{ files.length === 1 ? "1 PDF" : `${files.length} PDFs` }}
        </div>
      </div>

      <div class="alice-files__stat-tile">
        <div class="alice-files__stat-row">
          <span
            class="alice-files__stat-mark"
            :style="{
              background: finalReport
                ? 'var(--vp-turquoise-100)'
                : 'var(--vp-grijs-60)',
            }"
          />
          <span class="alice-files__stat-value">{{
            finalReport ? "Set" : "Not set"
          }}</span>
        </div>
        <div class="alice-files__stat-label">Final report</div>
        <div class="alice-files__stat-sub alice-files__stat-sub--ellipsis">
          {{ finalReport ? finalReport.name : "Pick the eindrapport" }}
        </div>
      </div>

      <div class="alice-files__stat-tile">
        <div class="alice-files__stat-row">
          <span
            class="alice-files__stat-mark"
            style="background: var(--vp-grijs-60)"
          />
          <span class="alice-files__stat-value">{{ storageLabel }}</span>
        </div>
        <div class="alice-files__stat-label">Storage used</div>
        <div class="alice-files__stat-sub">of 50 GB workspace</div>
      </div>
    </div>

    <!-- ============== Toolbar ============== -->
    <div class="alice-files__toolbar">
      <div class="alice-files__search">
        <Icon name="search" :size="18" />
        <input
          v-model="search"
          class="alice-files__search-input"
          type="text"
          placeholder="Search files…"
        />
        <span class="alice-files__search-count"
          >{{ filtered.length }} of {{ files.length }}</span
        >
      </div>
      <button class="alice-files__sort" @click="cycleSort">
        <Icon name="sort" :size="18" />
        <span>{{ sortLabel }}</span>
        <Icon name="chevron-down" :size="14" />
      </button>
    </div>

    <!-- ============== Upload progress ============== -->
    <div v-if="anyUploading" class="alice-files__uploads">
      <div
        v-for="(item, idx) in uploading.filter((u) => u.uploading)"
        :key="`upload-${idx}-${item.name}`"
        class="alice-files__upload-row"
      >
        <span class="alice-files__upload-name">{{ item.name }}</span>
        <div class="alice-files__upload-track">
          <div
            class="alice-files__upload-fill"
            :style="{ width: `${item.progress}%` }"
          />
        </div>
        <span class="alice-files__upload-pct"
          >{{ Math.round(item.progress) }}%</span
        >
      </div>
    </div>

    <!-- ============== Table ============== -->
    <div class="alice-files__table">
      <div class="alice-files__table-head">
        <div>#</div>
        <div>Name</div>
        <div>Type</div>
        <div class="alice-files__th-right">Size</div>
        <div>Created</div>
        <div class="alice-files__th-center">Final</div>
        <div></div>
      </div>

      <div v-if="!filtered.length" class="alice-files__empty">
        <p v-if="files.length">
          No files match
          <span class="alice-files__empty-q">"{{ search }}"</span>.
        </p>
        <p v-else>
          No files yet. Drop PDFs in the zone below or click “Upload file”.
        </p>
      </div>

      <div
        v-for="(file, idx) in filtered"
        :key="file.id || file.url || idx"
        class="alice-files__row"
        :class="{ 'alice-files__row--final': file.isFinalReport }"
      >
        <div class="alice-files__cell-num">
          {{ String(idx + 1).padStart(2, "0") }}
        </div>

        <div class="alice-files__cell-name">
          <a
            class="alice-files__name-link"
            :href="file.url"
            target="_blank"
            rel="noopener"
            :title="file.name"
          >
            <span class="alice-files__name">{{ file.name }}</span>
            <Icon name="external" :size="12" class="alice-files__name-ext" />
          </a>
        </div>

        <div class="alice-files__cell-type">
          <select
            v-if="!isGuest"
            class="alice-files__type-select"
            :value="effectiveType(file)"
            @change="setFileType(file, $event.target.value)"
          >
            <option value="">— Untyped</option>
            <option v-for="t in FILE_TYPES" :key="t.key" :value="t.key">
              {{ t.label }}
            </option>
          </select>
          <span v-else>{{ typeLabel(effectiveType(file)) }}</span>
        </div>

        <div class="alice-files__cell-size">{{ formatBytes(file.size) }}</div>

        <div class="alice-files__cell-created">
          <div>{{ createdDate(file.createdAt) }}</div>
          <div
            v-if="createdTime(file.createdAt)"
            class="alice-files__created-sub"
          >
            {{ createdTime(file.createdAt) }}
            <template v-if="file.uploadedBy"> · {{ file.uploadedBy }}</template>
          </div>
        </div>

        <div class="alice-files__cell-final">
          <button
            v-if="!isGuest"
            class="alice-files__star"
            :class="{
              'alice-files__star--on': effectiveType(file) === 'report',
            }"
            :title="
              effectiveType(file) === 'report'
                ? 'Unmark as final report'
                : 'Mark as final report'
            "
            @click="
              setFileType(
                file,
                effectiveType(file) === 'report' ? '' : 'report'
              )
            "
          >
            <Icon
              name="star"
              :size="18"
              :filled="effectiveType(file) === 'report'"
            />
          </button>
        </div>

        <div class="alice-files__cell-actions">
          <a
            class="alice-files__action"
            :href="file.url"
            target="_blank"
            rel="noopener"
            title="Open in new tab"
            @click.stop
          >
            <Icon name="arrow-right" :size="14" />
          </a>
          <button
            v-if="!isGuest"
            class="alice-files__action alice-files__action--danger"
            title="Delete"
            @click="deleteFile(file)"
          >
            <Icon name="trash" :size="14" />
          </button>
        </div>
      </div>
    </div>

    <!-- ============== Drop zone ============== -->
    <div
      class="alice-files__dropzone"
      :class="{ 'is-dragover': dragOver }"
      @click="triggerUpload"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <Icon name="plus" :size="20" />
      <div class="alice-files__dropzone-text">
        Drop PDF files here, or
        <span class="alice-files__dropzone-link">click to browse</span>
      </div>
      <div class="alice-files__dropzone-sub">PDF only · multiple at once</div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { storage, db } from "@/firebase";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  updateDoc,
} from "firebase/firestore";

import Icon from "@/components/alice/Icon.vue";

const SORTS = [
  { key: "recent", label: "Most recent" },
  { key: "oldest", label: "Oldest first" },
  { key: "alpha", label: "A → Z" },
];

const FILE_TYPES = [
  { key: "proposal", label: "Proposal" },
  { key: "test_script", label: "Test script" },
  { key: "report", label: "Report" },
];

export default {
  name: "ProjectFiles",
  components: { Icon },
  data() {
    return {
      search: "",
      sortIndex: 0,
      uploading: [],
      dragOver: false,
      FILE_TYPES,
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    files() {
      return this.project?.files || [];
    },
    sortLabel() {
      return SORTS[this.sortIndex].label;
    },
    sortKey() {
      return SORTS[this.sortIndex].key;
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      let list = this.files;
      if (q) {
        list = list.filter((f) => (f.name || "").toLowerCase().includes(q));
      }
      const copy = [...list];
      if (this.sortKey === "alpha") {
        copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      } else if (this.sortKey === "oldest") {
        copy.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      } else {
        copy.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      }
      return copy;
    },
    finalReport() {
      return this.files.find((f) => f.isFinalReport);
    },
    totalSize() {
      return this.files.reduce((s, f) => s + (Number(f.size) || 0), 0);
    },
    storageLabel() {
      return this.formatBytes(this.totalSize, 1) || "0 B";
    },
    anyUploading() {
      return this.uploading.some((u) => u.uploading);
    },
  },
  methods: {
    cycleSort() {
      this.sortIndex = (this.sortIndex + 1) % SORTS.length;
    },
    typeLabel(key) {
      const t = FILE_TYPES.find((x) => x.key === key);
      return t ? t.label : "— Untyped";
    },
    triggerUpload() {
      if (this.$refs.fileInput) this.$refs.fileInput.click();
    },
    formatBytes(bytes, digits = 0) {
      if (!bytes || bytes < 0) return "—";
      const units = ["B", "KB", "MB", "GB", "TB"];
      let i = 0;
      let n = bytes;
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024;
        i++;
      }
      const out = i >= 3 ? n.toFixed(digits || 1) : Math.round(n);
      return `${out} ${units[i]}`;
    },
    formatLabel(contentType) {
      if (!contentType) return "FILE";
      if (contentType === "application/pdf") return "PDF";
      return contentType.split("/").pop().toUpperCase();
    },
    createdDate(unix) {
      if (!unix) return "—";
      const d = new Date(unix * 1000);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    createdTime(unix) {
      if (!unix) return "";
      const d = new Date(unix * 1000);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    onFileChange(e) {
      const files = Array.from(e.target.files || []);
      this.processFiles(files);
      e.target.value = "";
    },
    onDrop(event) {
      this.dragOver = false;
      const files = Array.from(event.dataTransfer?.files || []);
      this.processFiles(files);
    },
    processFiles(files) {
      files.forEach((file) => {
        if (!file) return;
        if (file.type !== "application/pdf") {
          alert(`Only PDF files are allowed (skipping ${file.name})`);
          return;
        }
        this.uploadFile(file, this.uploading.length);
      });
    },

    async uploadFile(file, index) {
      this.uploading.push({
        index,
        uploading: true,
        progress: 0,
        name: file.name,
      });
      try {
        const projectId = this.$route.params.id || "unknown";
        const path = `projects/${projectId}/files/${Date.now()}-${file.name}`;
        const refObj = storageRef(storage, path);
        const uploadTask = uploadBytesResumable(refObj, file, {
          contentType: file.type,
        });

        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (this.uploading[index]) {
              this.uploading[index].progress = progress;
              if (progress === 100) {
                this.uploading[index].progress = 0;
                this.uploading[index].uploading = false;
              }
            }
          },
          (error) => {
            console.error("Upload error", error);
          }
        );

        await uploadTask;
        const downloadURL = await getDownloadURL(refObj);

        await addDoc(collection(db, "projects", projectId, "files"), {
          name: file.name,
          size: file.size,
          contentType: file.type,
          url: downloadURL,
          path,
          fileType: null,
          createdAt: Math.round(Date.now() / 1000),
        });

        await this.bumpStatusToInProgress(projectId);

        this.$store.dispatch("fetchProject", projectId);
      } catch (err) {
        console.error("Upload failed", err);
      } finally {
        if (this.uploading[index]) this.uploading[index].uploading = false;
      }
    },

    async bumpStatusToInProgress(projectId) {
      // Adding a file to a draft project means work has started — flip it.
      // Don't downgrade projects already marked as "done". Guests can't
      // write the project doc (rules) — skip instead of logging a denial.
      if (this.isGuest) return;
      const current = this.$store.state.project?.status;
      if (current && current !== "draft") return;
      try {
        await updateDoc(doc(db, "projects", projectId), {
          status: "in_progress",
        });
      } catch (err) {
        console.error("Failed to bump project status:", err);
      }
    },

    // Effective type of a file, honouring the legacy isFinalReport flag for
    // files uploaded before the fileType field existed.
    effectiveType(file) {
      if (file.fileType) return file.fileType;
      return file.isFinalReport ? "report" : "";
    },

    // Set a file's type. Selecting "report" demotes any other report (a project
    // has at most one). Changing the type clears extraction tracking so the next
    // "Save changes" re-extracts the file into the correct collection.
    async setFileType(file, type) {
      const projectId = this.$route.params.id;
      const normalized = type || null;
      try {
        if (normalized === "report") {
          const currentReport = this.files.find(
            (f) => f.id !== file.id && this.effectiveType(f) === "report"
          );
          if (currentReport) {
            await updateDoc(
              doc(db, "projects", projectId, "files", currentReport.id),
              {
                fileType: null,
                isFinalReport: false,
                extractedAt: null,
                extractedType: null,
                insightsProcessed: false,
              }
            );
          }
        }
        await updateDoc(doc(db, "projects", projectId, "files", file.id), {
          fileType: normalized,
          isFinalReport: normalized === "report",
          extractedAt: null,
          extractedType: null,
          insightsProcessed: false,
        });
        this.$store.dispatch("fetchProject", projectId);
      } catch (err) {
        console.error("Set file type failed", err);
      }
    },

    // Backward-compatible wrapper for any callers expecting the old name.
    async toggleFinalReport(fileItem, value) {
      await this.setFileType(fileItem, value ? "report" : null);
    },

    async deleteFile(fileItem) {
      if (!confirm(`Delete ${fileItem.name}?`)) return;
      try {
        if (fileItem.path) {
          try {
            await deleteObject(storageRef(storage, fileItem.path));
          } catch (storageErr) {
            // Storage object may already be missing — surface but continue.
            console.warn("Storage delete failed", storageErr);
          }
        }

        const projectId = this.$route.params.id;
        const filesRef = collection(db, "projects", projectId, "files");
        const snapshot = await getDocs(filesRef);

        for (const docSnapshot of snapshot.docs) {
          const data = docSnapshot.data();
          if (data.url === fileItem.url) {
            await deleteDoc(
              doc(db, "projects", projectId, "files", docSnapshot.id)
            );
            break;
          }
        }

        this.$store.dispatch("fetchProject", projectId);
      } catch (err) {
        console.error("Delete failed", err);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.alice-files {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  background: var(--vp-white);
}

/* ============== Header ============== */
.alice-files__head {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding: 32px 32px 24px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-files__head-main {
  flex: 1;
  min-width: 0;
}
.alice-files__crumb {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 8px;
}
.alice-files__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.05;
  color: var(--vp-black);
}
.alice-files__count {
  display: inline-block;
  margin-left: 12px;
  font-family: var(--font-ui);
  font-size: 16px;
  font-weight: 700;
  color: var(--fg-3);
  vertical-align: middle;
}
.alice-files__subtitle {
  margin: 8px 0 0;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--fg-2);
  max-width: 560px;
  line-height: 1.5;
}
.alice-files__upload {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 44px;
  padding: 0 20px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease;
}
.alice-files__upload:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-files__upload:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ============== Stats ============== */
.alice-files__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid var(--border-hairline);
}
.alice-files__stat-tile {
  position: relative;
  padding: 20px 32px;
  min-width: 0;
}
.alice-files__stat-tile + .alice-files__stat-tile {
  border-left: 1px solid var(--border-hairline);
}
.alice-files__stat-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.alice-files__stat-mark {
  width: 10px;
  height: 10px;
  align-self: center;
  flex-shrink: 0;
}
.alice-files__stat-value {
  font-family: var(--font-display);
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  color: var(--vp-black);
}
.alice-files__stat-label {
  margin-top: 8px;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  color: var(--vp-black);
}
.alice-files__stat-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.alice-files__stat-sub--ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ============== Toolbar ============== */
.alice-files__toolbar {
  display: flex;
  align-items: stretch;
  height: 56px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--vp-white);
}
.alice-files__search {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
  border-right: 1px solid var(--border-hairline);
  color: var(--vp-black);
}
.alice-files__search-input {
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: 16px;
  color: var(--vp-black);
}
.alice-files__search-input::placeholder {
  color: var(--fg-3);
}
.alice-files__search-count {
  flex-shrink: 0;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  white-space: nowrap;
}
.alice-files__sort {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
}
.alice-files__sort:hover {
  background: var(--bg-muted);
}

/* ============== Uploads ============== */
.alice-files__uploads {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--border-hairline);
  background: var(--accent-20);
}
.alice-files__upload-row {
  display: grid;
  grid-template-columns: 1fr minmax(160px, 320px) 56px;
  gap: 16px;
  align-items: center;
  padding: 12px 32px;
  font-family: var(--font-ui);
  font-size: 13px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-files__upload-row:last-child {
  border-bottom: none;
}
.alice-files__upload-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--vp-black);
  font-weight: 700;
}
.alice-files__upload-track {
  height: 6px;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
}
.alice-files__upload-fill {
  height: 100%;
  background: var(--vp-black);
  transition: width 0.2s ease;
}
.alice-files__upload-pct {
  font-family: var(--font-mono);
  font-size: 13px;
  text-align: right;
  color: var(--vp-black);
}

/* ============== Table ============== */
.alice-files__table {
  display: flex;
  flex-direction: column;
}
.alice-files__table-head,
.alice-files__row {
  display: grid;
  grid-template-columns: 56px minmax(0, 2.4fr) 80px 100px 160px 80px 90px;
  gap: 16px;
  align-items: center;
  padding: 0 32px;
}
.alice-files__th-right {
  text-align: right;
}
.alice-files__th-center {
  text-align: center;
}
.alice-files__table-head {
  height: 44px;
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  position: sticky;
  top: 0;
  z-index: 1;
}
.alice-files__row {
  position: relative;
  padding-top: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-hairline);
  transition: background 0.12s ease;
}
.alice-files__row:hover {
  background: var(--bg-muted);
}
.alice-files__row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
}
.alice-files__row:hover::before {
  background: var(--accent-100);
}
.alice-files__row--final::before {
  background: var(--accent-100);
}

.alice-files__cell-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 28px;
  background: var(--vp-grijs-20);
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--fg-3);
}

.alice-files__cell-name {
  min-width: 0;
}
.alice-files__name-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: var(--vp-black);
  max-width: 100%;
  min-width: 0;
}
.alice-files__name {
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 700;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-files__name-ext {
  color: var(--fg-3);
  flex-shrink: 0;
}
.alice-files__name-link:hover .alice-files__name {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.alice-files__name-link:hover .alice-files__name-ext {
  color: var(--vp-black);
}

.alice-files__cell-type {
  display: flex;
  align-items: center;
}
.alice-files__type-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-2);
  background: var(--vp-white);
}
.alice-files__type-select {
  padding: 4px 8px;
  border: 1px solid var(--border-hairline);
  border-radius: 0;
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-1);
  cursor: pointer;
  max-width: 130px;
}
.alice-files__type-select:hover {
  border-color: var(--fg-3);
}
.alice-files__type-select:focus {
  outline: none;
  border-color: var(--vp-black);
}

.alice-files__cell-size {
  font-family: var(--font-mono);
  font-size: 13px;
  color: var(--vp-black);
  text-align: right;
  letter-spacing: 0.04em;
}

.alice-files__cell-created {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
}
.alice-files__created-sub {
  font-size: 11px;
  color: var(--fg-3);
  margin-top: 2px;
}

.alice-files__cell-final {
  display: flex;
  justify-content: center;
}
.alice-files__star {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  border-radius: 0;
  color: var(--vp-grijs-60);
  transition: color 0.12s ease, border-color 0.12s ease;
}
.alice-files__star:hover {
  color: var(--vp-black);
  border-color: var(--vp-black);
}
.alice-files__star--on,
.alice-files__star--on:hover {
  color: var(--accent-100);
  border-color: transparent;
}

.alice-files__cell-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
.alice-files__action {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  color: var(--vp-black);
  border-radius: 0;
  text-decoration: none;
}
.alice-files__action:hover {
  border-color: var(--vp-black);
}
.alice-files__action--danger {
  color: var(--vp-rood-100);
}
.alice-files__action--danger:hover {
  border-color: var(--vp-rood-100);
  background: var(--vp-rood-10);
}

.alice-files__empty {
  padding: 32px;
  border-bottom: 1px solid var(--border-hairline);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--fg-3);
}
.alice-files__empty-q {
  background: var(--accent-20);
  padding: 1px 6px;
  color: var(--vp-black);
}

/* ============== Drop zone ============== */
.alice-files__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 24px 32px 32px;
  padding: 32px;
  background: var(--vp-white);
  border: 1px dashed var(--vp-grijs-60);
  cursor: pointer;
  text-align: center;
  font-family: var(--font-ui);
  color: var(--fg-3);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.alice-files__dropzone:hover,
.alice-files__dropzone.is-dragover {
  border-color: var(--vp-black);
  border-style: solid;
  color: var(--vp-black);
  background: var(--bg-muted);
}
.alice-files__dropzone-text {
  font-size: 14px;
  font-weight: 600;
}
.alice-files__dropzone-link {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.alice-files__dropzone-sub {
  font-size: 12px;
  color: var(--fg-3);
}

@media (max-width: 1279px) {
  .alice-files__head {
    padding: 24px 20px 20px;
  }
  .alice-files__stats {
    grid-template-columns: 1fr;
  }
  .alice-files__stat-tile + .alice-files__stat-tile {
    border-left: none;
    border-top: 1px solid var(--border-hairline);
  }
  .alice-files__table-head,
  .alice-files__row {
    grid-template-columns: 40px minmax(0, 2.4fr) 100px 80px 90px;
    padding: 0 20px;
  }
  .alice-files__table-head > div:nth-child(3),
  .alice-files__table-head > div:nth-child(5),
  .alice-files__row .alice-files__cell-type,
  .alice-files__row .alice-files__cell-created {
    display: none;
  }
}
</style>
